import {displayApi,invalidateOperatorSummary} from '@/lib/forex-display-api';
import { useCallback, useEffect, useState } from "react";
import { configAgentError, forexApi, type ConfigReviewV1 } from "@/lib/forex-api";

const labels: Record<string, string> = {
  strategy_key: "策略算法", timeframe_config: "周期配置", category: "策略类别", symbol: "适用品种", cadence_profile_id: "运行周期",
  allowed_actions: "允许方向", fixed_lot: "固定手数", max_lot_per_order: "单笔手数上限",
  max_total_lots: "总手数上限", max_open_positions: "最大持仓数",
  max_daily_loss: "日亏损上限", min_free_margin: "最低可用保证金",
  master_decision: "决策原则", technical_analyst: "技术分析重点", risk_analyst: "风险评估重点",
  stop_loss: "止损设置", take_profit: "止盈设置",
};
const reviewStatusLabels: Record<ConfigReviewV1['status'], string> = {
  pending: '待审核', approved: '已通过', rejected: '已拒绝', superseded: '已被新版本替代',
};

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function reviewSnapshot(payload?: Record<string, unknown>) {
  const strategy = record(Array.isArray(payload?.strategy_configs) ? payload.strategy_configs[0] : null);
  const rules = record(strategy.rule_snapshot);
  const team = record(strategy.ai_team_snapshot);
  const roles = Array.isArray(team.roles) ? team.roles.map(record) : [];
  return {
    trade: {
      category: rules.category, symbol: strategy.symbol,
      strategy_key: record(rules.strategy_spec).strategy_key,
      timeframe_config: record(rules.strategy_spec).timeframe_config,
      cadence_profile_id: strategy.cadence_profile_id,
      stop_loss: record(strategy.protection_policy).stop_loss ?? "ai_decides",
      take_profit: record(strategy.protection_policy).take_profit ?? "ai_decides",
      allowed_actions: record(strategy.market_runtime).allowed_actions,
      ...record(rules.common_rules), ...record(record(rules.strategy_spec).parameters),
    },
    prompts: Object.fromEntries(roles.map((role) => [String(role.role_id), role.instruction])),
  };
}

function changeGroup(title: string, before: Record<string, unknown>, after: Record<string, unknown>) {
  const changed = [...new Set([...Object.keys(before), ...Object.keys(after)])]
    .filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
  if (!changed.length) return null;
  const display = (value: unknown) => value == null ? "无" : value === "none" ? "不设置" : value === "ai_decides" ? "AI 决定" : Array.isArray(value) ? value.join("、") : typeof value === "object" ? JSON.stringify(value) : String(value);
  return <div className="space-y-1"><h4 className="font-medium">{title}</h4>{changed.map((key) =>
    <p key={key} className="break-all whitespace-pre-wrap">{labels[key] || key}：{display(before[key])} → {display(after[key])}</p>
  )}</div>;
}

export default function ConfigAgentReviewTab({initialReviewId}: {initialReviewId?: string}) {
  const [history,setHistory]=useState(false);
  const [cursor,setCursor]=useState<string|null>(null);
  const [reviews, setReviews] = useState<ConfigReviewV1[]>([]);
  const [selected, setSelected] = useState<ConfigReviewV1 | null>(null);
  const [reviewRisks, setReviewRisks] = useState<{ before: Record<string, unknown>; after: Record<string, unknown> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [requestIds, setRequestIds] = useState<Record<string, string>>({});
  const [search,setSearch]=useState('');

  const refresh = useCallback(async () => {
    setBusy(true);
    setReviews([]);
    setCursor(null);
    try {
      if(history){const data=await forexApi.listConfigReviews();setReviews(data.items);setCursor(null);}
      else{const data=await displayApi.todos('agent_review');const pending=await Promise.all(data.items.map(item=>forexApi.getConfigReview(item.subject_id)));setReviews(pending);setCursor(data.next_cursor);}
      setError(null);
    } catch {
      setError("审核列表读取失败，请刷新后重试");
    } finally { setBusy(false); }
  }, [history]);
  useEffect(() => { void refresh(); }, [refresh]);
  const more=async()=>{if(!cursor||busy)return;setBusy(true);try{const data=await displayApi.todos('agent_review',cursor);const rows=await Promise.all(data.items.map(item=>forexApi.getConfigReview(item.subject_id)));setReviews(previous=>[...new Map([...previous,...rows].map(row=>[row.review_id,row])).values()]);setCursor(data.next_cursor);}catch{setError('下一页读取失败，请重试');}finally{setBusy(false);}};

  const openReview = useCallback(async (review: Pick<ConfigReviewV1, "review_id">) => {
    setBusy(true);
    try {
      const detail = await forexApi.getConfigReview(review.review_id);
      const riskFromSource = async (payload?: Record<string, unknown>) => {
        if (!payload) return {};
        const ref = record(payload.strategy_template_ref);
        if (!ref.id || !ref.version || !ref.content_hash) throw new Error("REVIEW_STRATEGY_REF_MISSING");
        const template = await forexApi.getConfigTemplateRevision(String(ref.id), Number(ref.version));
        if (template.template_ref.content_hash !== ref.content_hash) throw new Error("REVIEW_STRATEGY_REF_CHANGED");
        const risk = record(record(template.editable_payload).risk);
        if (!Object.keys(risk).length) throw new Error("REVIEW_RISK_SNAPSHOT_MISSING");
        return risk;
      };
      const [beforeRisk, afterRisk] = await Promise.all([
        riskFromSource(detail.previous_config?.payload), riskFromSource(detail.frozen_config?.payload),
      ]);
      setReviewRisks({ before: beforeRisk, after: afterRisk });
      setSelected(detail);
      setError(null);
    } catch {
      setSelected(null); setReviewRisks(null);
      setError("审核来源策略或风控快照无法核实，请刷新后重试；当前不能审批");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { if(initialReviewId) void openReview({review_id: initialReviewId}); }, [initialReviewId, openReview]);

  const decide = async (decision: "approve" | "reject") => {
    if (!selected || busy) return;
    const keyId = `${selected.review_id}:${decision}`;
    const key = requestIds[keyId] || crypto.randomUUID();
    setRequestIds((current) => ({ ...current, [keyId]: key }));
    setBusy(true);
    try {
      const result = await forexApi.decideConfigReview(selected, decision, key);
      setSelected(null);
      setRequestIds((current) => { const next = { ...current }; delete next[keyId]; return next; });
      setMessage(result.status === "approved" ? "审核已通过，配置已生效。运行状态请单独查看。" : "审核已拒绝");
      invalidateOperatorSummary();await refresh();
    } catch (cause) {
      try {
        const current = await forexApi.getConfigReview(selected.review_id);
        const expected = decision === "approve" ? "approved" : "rejected";
        if (current.status === expected) {
          setSelected(null);
          setRequestIds((previous) => { const next = { ...previous }; delete next[keyId]; return next; });
          setMessage(expected === "approved" ? "审核已通过，配置已生效。运行状态请单独查看。" : "审核已拒绝");
          invalidateOperatorSummary();await refresh();
          return;
        }
      } catch { /* Keep the same idempotency key when the result cannot be read. */ }
      setError(configAgentError(cause, "审核结果尚未确认，请使用当前操作标识重试或刷新状态"));
    } finally {
      setBusy(false);
    }
  };

  const before = reviewSnapshot(selected?.previous_config?.payload);
  const after = reviewSnapshot(selected?.frozen_config?.payload);
  const reviewedPayload = record(selected?.frozen_config?.payload);
  const reviewedTarget = record(reviewedPayload.platform_execution_target);
  const reviewedStrategy = record(reviewedPayload.strategy_template_ref);
  const visibleReviews = reviews.filter(review => !search || `${review.agent_id} ${review.review_id}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));

  return <div className="space-y-4">
    <div className="flex items-center justify-between"><h2 className="text-lg font-semibold">智能体审核</h2>
      <button disabled={busy} onClick={() => void refresh()} className="px-3 py-1.5 rounded-lg border">刷新</button></div>
    <div className="flex gap-4"><button disabled={busy} className={!history?'text-brand':''} onClick={()=>setHistory(false)}>待审核</button><button disabled={busy} className={history?'text-brand':''} onClick={()=>setHistory(true)}>最近审核记录</button></div>
    <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted"><input aria-label="查找已加载审核" value={search} onChange={event=>setSearch(event.target.value)} placeholder="智能体 ID 或审核 ID" className="rounded-lg border border-surface-border px-3 py-2 text-sm"/><span>当前加载 {reviews.length} 条 · 显示 {visibleReviews.length} 条{cursor?'；可继续加载待审事项':''}</span></div>
    {cursor&&<button disabled={busy} onClick={()=>void more()}>加载更多待审核事项</button>}
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {message && <p role="status" className="text-sm text-emerald-600">{message}</p>}
    <div className="rounded-xl border border-surface-border bg-white overflow-x-auto">
      <table className="w-full text-sm"><thead><tr className="border-b text-left bg-surface/50">
        <th className="p-3">智能体</th><th className="p-3">提交时间</th><th className="p-3">审核状态</th><th className="p-3">操作</th>
      </tr></thead><tbody>
        {visibleReviews.map((review) => <tr key={review.review_id} className="border-b border-surface-border">
          <td className="p-3 font-mono">{review.agent_id}<small className="block text-text-muted">审核 {review.review_id}</small></td>
          <td className="p-3">{new Date(review.submitted_at * 1000).toLocaleString("zh-CN")}</td>
          <td className="p-3">{reviewStatusLabels[review.status] || '状态待确认'}</td>
          <td className="p-3"><button disabled={busy}
            onClick={() => void openReview(review)} className="px-3 py-1.5 rounded-lg bg-brand/10 text-brand disabled:opacity-50">
            {review.status === "pending" ? "查看并审核" : "查看"}</button></td>
        </tr>)}
        {!busy&&!error&&!visibleReviews.length&&<tr><td colSpan={4} className="p-8 text-center text-text-muted">{reviews.length?'当前加载记录中没有符合条件的审核':history?'暂无最近审核记录':'当前没有待审核事项'}</td></tr>}
      </tbody></table>
    </div>
    {selected && <div role="dialog" aria-modal="true" aria-label="审核配置" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl p-6 space-y-4 max-h-[85vh] overflow-auto">
        <h3 className="text-lg font-semibold">审核智能体配置</h3>
        <p className="text-sm">智能体 {selected.agent_id} · 配置 v{selected.frozen_config?.config_version ?? "?"}</p>
        <p className="text-xs font-mono break-all">配置哈希：{selected.frozen_config?.content_hash ?? "读取中"}</p>
        {selected.template_changes && <div className="rounded border p-3 text-xs space-y-2">
          <p>策略模板：{selected.template_changes.before?.strategy
            ? `${selected.template_changes.before.strategy.id} · v${selected.template_changes.before.strategy.version}` : "无生效版本"} → {selected.template_changes.after?.strategy
            ? `${selected.template_changes.after.strategy.id} · v${selected.template_changes.after.strategy.version}` : "无策略模板"}</p>
          <p className="break-all">风控快照：{selected.template_changes.before?.risk_hash ?? "无生效快照"} → {selected.template_changes.after?.risk_hash ?? "无风控快照"}</p>
        </div>}
        <div className="rounded border p-3 text-xs space-y-1">
          <p>研究策略来源：{String(reviewedStrategy.id || "未记录")} · v{String(reviewedStrategy.version || "—")}</p>
          <p>研究规则与 AI 团队使用此冻结策略；平台账号 {String(reviewedTarget.account_scope || "未记录")} 仅为一个执行目标。</p>
          <p className="text-amber-700">审核通过仅允许新研究使用此版本；账号是否参与、授权是否有效以及逐账号执行许可仍分别判定。</p>
        </div>
        <div className="rounded border p-3 text-xs space-y-3">
          {changeGroup("交易参数变化", before.trade, after.trade)}
          {changeGroup("逐账号风控边界变化", reviewRisks?.before || {}, reviewRisks?.after || {})}
          {changeGroup("AI 指令变化", before.prompts, after.prompts)}
        </div>
        <details className="rounded-lg border p-3 text-xs"><summary className="cursor-pointer">查看冻结配置 JSON</summary><pre className="mt-2 whitespace-pre-wrap break-all">{JSON.stringify(selected.frozen_config?.payload ?? {}, null, 2)}</pre></details>
        <div className="flex justify-end gap-2"><button onClick={() => setSelected(null)} className="px-4 py-2 rounded-lg border">取消</button>
          <button disabled={busy||selected.status!=="pending"} onClick={() => void decide("reject")} className="px-4 py-2 rounded-lg border text-red-600">拒绝</button>
          <button disabled={busy||selected.status!=="pending"} onClick={() => void decide("approve")} className="px-4 py-2 rounded-lg bg-emerald-600 text-white">上架通过</button></div>
      </div>
    </div>}
  </div>;
}
